import React from 'react';

export interface TeamPlayerDisplay {
  tournament_player_id: number;
  id?: number;
  player_id?: string;
  name: string;
  player_name?: string;
  role: string;
  team: string;
  team_name?: string;
  credits: number;
  is_captain: boolean;
  is_vice_captain: boolean;
  /** When true, show foreign-player indicator (e.g. ✈) next to name */
  isForeign?: boolean;
}

const ROLE_ORDER: Record<string, number> = {
  Batsman: 1,
  'WK-Batsman': 2,
  'All-Rounder': 3,
  Bowler: 4
};

function getRoleOrder(role: string): number {
  return ROLE_ORDER[role] ?? 5;
}

function getRoleIcon(role: string): React.ReactNode {
  switch (role) {
    case 'Batsman':
      return (
        <img
          src="/images/baticon.png"
          alt="Bat"
          className="inline-block w-10 h-10 object-contain"
          style={{ display: 'inline-block', verticalAlign: 'middle' }}
        />
      );
    case 'Bowler':
      return '🥎';
    case 'WK-Batsman':
      return '🧤';
    case 'All-Rounder':
      return '🏏';
    default:
      return '👤';
  }
}

function getTeamShortName(team: string | undefined): string {
  if (!team) return '';
  return team.slice(0, 3).toUpperCase();
}

export interface PlayingXIViewProps {
  players: TeamPlayerDisplay[];
  viewMode: 'list' | 'field';
  onViewModeChange: (mode: 'list' | 'field') => void;
  canEdit: boolean;
  creditsTotal: number;
  creditsCap: number;
  captainId: number | null;
  viceCaptainId: number | null;
  onSetCaptain?: (tournamentPlayerId: number) => void;
  onSetViceCaptain?: (tournamentPlayerId: number) => void;
  onRemovePlayer?: (player: TeamPlayerDisplay) => void;
  onAddSlot?: () => void;
  emptySlotsCount?: number;
  matchPointsLabel?: React.ReactNode;
}

export const PlayingXIView: React.FC<PlayingXIViewProps> = ({
  players,
  viewMode,
  onViewModeChange,
  canEdit,
  creditsTotal,
  creditsCap,
  captainId,
  viceCaptainId,
  onSetCaptain,
  onSetViceCaptain,
  onRemovePlayer,
  onAddSlot,
  emptySlotsCount = 0,
  matchPointsLabel
}) => {
  const sortedPlayers = [...players].sort((a, b) => getRoleOrder(a.role) - getRoleOrder(b.role));
  const roleGroups: { role: string; label: string; players: TeamPlayerDisplay[] }[] = [
    { role: 'WK-Batsman', label: 'Wicket-keepers', players: sortedPlayers.filter(p => p.role === 'WK-Batsman') },
    { role: 'Batsman', label: 'Batsmen', players: sortedPlayers.filter(p => p.role === 'Batsman') },
    { role: 'All-Rounder', label: 'All-Rounders', players: sortedPlayers.filter(p => p.role === 'All-Rounder') },
    { role: 'Bowler', label: 'Bowlers', players: sortedPlayers.filter(p => p.role === 'Bowler') }
  ];

  const playerId = (p: TeamPlayerDisplay) => p.tournament_player_id ?? p.id ?? 0;
  const creditsStr = (credits: number) => Number(credits) === Math.floor(credits) ? String(credits) : credits.toFixed(1);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-800">Playing XI</h3>
          <div className="flex rounded-lg border border-gray-300 bg-gray-100 p-0.5" role="tablist">
            <button
              type="button"
              onClick={() => onViewModeChange('list')}
              className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange('field')}
              className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                viewMode === 'field' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Field
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            Credits: <span className={`font-bold ${creditsTotal <= creditsCap ? 'text-green-600' : 'text-red-600'}`}>{creditsTotal.toFixed(1)}/{creditsCap}</span>
          </div>
          {matchPointsLabel}
        </div>
      </div>

      {viewMode === 'field' ? (
        <div
          className="rounded-2xl border-2 border-green-600 shadow-inner text-center relative min-h-[160px] overflow-visible"
          style={{ backgroundImage: 'repeating-linear-gradient(90deg, #22c55e 0px, #22c55e 120px, #16a34a 120px, #16a34a 240px)' }}
        >
          <div className="relative p-2 sm:p-3 md:p-4 max-w-4xl mx-auto overflow-visible z-[1]">
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[20%] min-w-[64px] max-w-[104px] h-[52%] min-h-[120px] rounded-sm border-2 border-amber-200/90 pointer-events-none shadow-inner z-0"
              style={{ background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 30%, #fcd34d 50%, #fde68a 70%, #fef3c7 100%)' }}
              aria-hidden
            />
            {roleGroups.map(({ role, label, players: groupPlayers }) => (
              <div key={role} className="relative mb-1 sm:mb-1.5 last:mb-0 flex flex-col items-center z-[1]">
                <div className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider mb-0.5 sm:mb-1 text-center w-full" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{label}</div>
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 justify-center items-center">
                  {groupPlayers.map((player) => {
                    const pid = playerId(player);
                    const playerIconUrl = `${process.env.PUBLIC_URL || ''}/PlayerIcon.png`;
                    return (
                      <div key={pid} className="flex flex-col items-center relative">
                        <div className="flex flex-row items-center gap-0">
                          <div className="relative inline-block">
                            <img src={playerIconUrl} alt="" className="w-24 h-24 sm:w-28 sm:h-28 object-contain block" style={{ objectFit: 'contain' }} />
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-0.5 px-1.5 py-0.5 rounded bg-green-900/90 text-white text-xs font-bold whitespace-nowrap" style={{ textShadow: '0 0 1px rgba(0,0,0,0.8)' }}>–</div>
                            {canEdit && onRemovePlayer && (
                              <button type="button" className="absolute -top-0.5 -right-0.5 z-20 w-7 h-7 sm:w-8 sm:h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-base font-bold shadow-md border-2 border-white" title="Remove" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemovePlayer(player); }}>−</button>
                            )}
                          </div>
                        </div>
                          <div className="-mt-4 sm:-mt-5 text-center">
                          <div className="font-bold text-sm text-green-900 truncate max-w-[100px] flex items-center justify-center gap-1 mx-auto flex-wrap">
                            {(player.name || player.player_name || '').trim() || '–'}
                            {player.isForeign && <span className="text-orange-600" title="Foreign player" aria-label="Foreign player">✈</span>}
                            {player.is_captain && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-700 text-xs font-bold rounded border border-yellow-500/30">C</span>}
                            {player.is_vice_captain && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-700 text-xs font-bold rounded border border-blue-500/30">VC</span>}
                          </div>
                          <div className="text-green-600 text-xs font-medium">{creditsStr(player.credits)} Cr</div>
                        </div>
                      </div>
                    );
                  })}
                  {canEdit && onAddSlot && emptySlotsCount > 0 && role === 'Bowler' && (
                    <div className="flex flex-col items-center relative z-10" key="add-slot">
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddSlot(); }} className="w-14 h-14 sm:w-16 sm:h-16 min-w-[56px] min-h-[56px] flex items-center justify-center text-2xl font-bold bg-green-600 hover:bg-green-500 text-white border-2 border-dashed border-green-400 shadow-md cursor-pointer rounded" title="Add player">+</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedPlayers.map((player, index) => {
            const pid = playerId(player);
            return (
              <div key={pid} className={`rounded-lg p-3 flex items-center justify-between bg-white/90 border border-gray-300`}>
                <div className="flex-1 flex items-center space-x-2 flex-wrap">
                  <span className={player.role === 'Bowler' ? 'text-sm' : 'text-lg'}>{getRoleIcon(player.role)}</span>
                  <div className="font-semibold text-gray-800 flex items-center gap-1">
                    {player.name || player.player_name || '–'}
                    {player.isForeign && <span className="text-orange-600" title="Foreign player" aria-label="Foreign player">✈</span>}
                    <span className="text-sm text-gray-600">({getTeamShortName(player.team || player.team_name)})</span>
                  </div>
                  <div className="text-sm text-gray-600">{creditsStr(player.credits)} cr</div>
                  {pid === captainId && <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-600 text-xs font-bold rounded">C</span>}
                  {pid === viceCaptainId && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-600 text-xs font-bold rounded">VC</span>}
                </div>
                <div className="flex items-center space-x-2">
                  {canEdit && (
                    <>
                      {onSetCaptain && (
                        <button type="button" onClick={() => onSetCaptain(pid)} className={`px-2 py-1 rounded text-xs font-medium ${pid === captainId ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}>C</button>
                      )}
                      {onSetViceCaptain && (
                        <button type="button" onClick={() => onSetViceCaptain(pid)} className={`px-2 py-1 rounded text-xs font-medium ${pid === viceCaptainId ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}>VC</button>
                      )}
                      {onRemovePlayer && (
                        <button type="button" onClick={() => onRemovePlayer(player)} className="bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold" title="Remove">−</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {canEdit && emptySlotsCount > 0 && onAddSlot && Array.from({ length: emptySlotsCount }).map((_, i) => (
            <div key={`empty-${i}`} className="rounded-lg p-3 flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300">
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddSlot(); }} className="bg-green-600 hover:bg-green-700 text-white rounded-full w-10 h-10 min-w-[40px] min-h-[40px] flex items-center justify-center text-2xl font-bold cursor-pointer" title="Add player">+</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlayingXIView;
